import { Loader2, Paperclip } from "lucide-react";
import { CardContent } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { fmtKb, AttachmentActionIcon } from "@/pages/applications/inboxShared";
import { useIsPhone } from "@/hooks/useIsPhone";

export function AttachmentsStrip({ atts, messageId, onPreview, fetchBlob }) {
  if (!atts?.length) return null;
  return (
    <div className="flex flex-wrap gap-1.5 px-4 py-2 border-b" data-testid="reading-pane-attachments">
      {atts.map(a => (
        <span key={a.id}
          className={cn("inline-flex items-center gap-1.5 border rounded-full pl-2 pr-1.5 py-0.5 text-[10px] bg-white",
            a.from_zip && "border-dashed")}
          title={a.name}>
          <Paperclip className="w-2.5 h-2.5 text-slate-400" />
          <span className="max-w-[180px] truncate">{a.name}</span>
          {a.pasted && (
            <span className="text-[9px] text-violet-700 bg-violet-50 border border-violet-200 rounded px-1" data-testid={`att-pasted-${a.id}`}>pasted</span>
          )}
          <span className="text-muted-foreground">{fmtKb(a.size)}</span>
          <AttachmentActionIcon messageId={messageId} att={a} onPreview={onPreview} fetchBlob={fetchBlob} />
        </span>
      ))}
    </div>
  );
}

const PHONE_FIT_CSS = "html,body{max-width:100%!important;overflow-x:hidden;word-break:break-word;-webkit-text-size-adjust:100%;margin:0;padding:8px}" +
  "img,video{max-width:100%!important;height:auto!important}table{max-width:100%!important;width:auto!important;table-layout:auto}td,th{word-break:break-word}pre{white-space:pre-wrap}";

// Sandboxed body iframe; `onSelection` receives any text the reader selects inside the email (used by the Slack snap).
// On a phone the frame fits the email to the screen width and grows to its content height so the page scrolls as one.
export function EmailBodyFrame({ html, loading, onSelection, heightClass = "h-[calc(100vh-430px)]" }) {
  const isPhone = useIsPhone();
  const fit = (frame) => {
    try {
      const doc = frame.contentDocument;
      if (!doc) return;
      if (!doc.getElementById("sf-fit")) { const st = doc.createElement("style"); st.id = "sf-fit"; st.textContent = PHONE_FIT_CSS; (doc.head || doc.documentElement).appendChild(st); }
      const h = Math.max(160, doc.documentElement.scrollHeight, doc.body?.scrollHeight || 0) + 24;
      frame.style.height = `${h}px`;
      doc.querySelectorAll("img").forEach((img) => { if (!img.complete) img.addEventListener("load", () => fit(frame), { once: true }); });
    } catch { /* sandboxed */ }
  };
  return (
    <CardContent className="p-0">
      {loading ? (
        <div className="p-8 text-center"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></div>
      ) : (
        <iframe
          title="email-body"
          sandbox="allow-same-origin"
          srcDoc={html || "<p>(no body)</p>"}
          className={cn("w-full bg-white", isPhone ? "min-h-[160px]" : heightClass)}
          data-testid="reading-pane-body"
          onLoad={(ev) => {
            if (isPhone) fit(ev.target);
            if (!onSelection) return;
            try {
              const doc = ev.target.contentDocument;
              const track = () => {
                const s = (doc.getSelection?.()?.toString() || "").trim();
                if (s) onSelection(s);
              };
              doc.addEventListener("mouseup", track);
              doc.addEventListener("selectionchange", track);
            } catch { /* sandboxed */ }
          }}
        />
      )}
    </CardContent>
  );
}
